import React from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useDelivery } from "../hooks/useDelivery";
import { X, Trash2, Minus, Plus, ShoppingBag } from "lucide-react";

export const CartDrawer = () => {
  const { t, cart, cartOpen, setCartOpen, updateCartItem, removeCartItem, subtotal } = useApp();
  const navigate = useNavigate();
  const { feeFor, freeOver, isFreePeriod, freeUntil } = useDelivery();
  const fee = feeFor(subtotal);
  const total = subtotal + fee;
  const remaining = Math.max(0, freeOver - subtotal);

  if (!cartOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-ha-in" onClick={() => setCartOpen(false)} />
      <div
        data-testid="cart-drawer"
        className="fixed inset-y-0 end-0 z-50 w-full max-w-md bg-card border-s border-border shadow-2xl flex flex-col animate-ha-in"
      >
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-head text-xl font-bold flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-primary" /> {t.cart}
          </h2>
          <button data-testid="cart-close-button" onClick={() => setCartOpen(false)} className="h-9 w-9 rounded-lg hover:bg-accent grid place-items-center">
            <X className="h-5 w-5" />
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 grid place-items-center text-muted-foreground p-8 text-center">
            <div>
              <ShoppingBag className="h-12 w-12 mx-auto mb-3 opacity-30" />
              {t.emptyCart}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
            {cart.map((item, i) => (
              <div key={i} data-testid={`cart-item-${i}`} className="rounded-2xl border border-border p-3 bg-background">
                <div className="flex gap-3">
                  <div className="h-14 w-14 rounded-xl bg-accent grid place-items-center text-2xl shrink-0 overflow-hidden">
                    {item.image ? <img src={item.image} alt="" className="h-full w-full object-cover" /> : item.emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{item.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{item.store_name}</div>
                      </div>
                      <button data-testid={`cart-remove-${i}`} onClick={() => removeCartItem(i)} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1 rounded-lg border border-border">
                        <button data-testid={`cart-dec-${i}`} onClick={() => item.qty > 1 && updateCartItem(i, { qty: item.qty - 1 })} className="h-8 w-8 grid place-items-center hover:bg-accent rounded-s-lg"><Minus className="h-3.5 w-3.5" /></button>
                        <span className="w-7 text-center text-sm font-semibold">{item.qty}</span>
                        <button data-testid={`cart-inc-${i}`} onClick={() => updateCartItem(i, { qty: item.qty + 1 })} className="h-8 w-8 grid place-items-center hover:bg-accent rounded-e-lg"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="font-bold text-primary">{item.price * item.qty} {t.kr}</div>
                    </div>
                  </div>
                </div>
                <input
                  data-testid={`cart-note-${i}`}
                  value={item.note || ""}
                  onChange={(e) => updateCartItem(i, { note: e.target.value })}
                  placeholder={t.notePlaceholder}
                  className="mt-2 w-full h-9 rounded-lg border border-border bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            ))}
          </div>
        )}

        {cart.length > 0 && (
          <div className="border-t border-border p-5 space-y-3">
            {isFreePeriod ? (
              <div data-testid="cart-free-banner" className="text-xs font-semibold text-primary">🎉 {t.freeDelivery}{freeUntil ? ` (t.o.m. ${freeUntil})` : ""}</div>
            ) : fee === 0 ? (
              <div data-testid="cart-free-banner" className="text-xs font-semibold text-primary">🎉 {t.freeDeliveryOver}</div>
            ) : remaining > 0 ? (
              <div data-testid="cart-free-hint" className="text-xs font-semibold text-muted-foreground">{remaining} {t.kr} {t.toFree}</div>
            ) : null}
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{t.subtotal}</span><span>{subtotal} {t.kr}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t.deliveryFee}</span><span>{fee === 0 ? t.free : `${fee} ${t.kr}`}</span></div>
              <div className="flex justify-between font-bold text-base pt-1"><span>{t.total}</span><span>{total} {t.kr}</span></div>
            </div>
            <button
              data-testid="cart-checkout-button"
              onClick={() => { setCartOpen(false); navigate("/checkout"); }}
              className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-primary/25"
            >
              {t.goCheckout} · {total} {t.kr}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
